import {
  useEffect,
  useRef,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  AddCalendarIcon,
  AffinityIcon,
  CommentIcon,
  DirectionIcon,
  EventMarkerIcon,
  MarkerGenericIcon,
  SaveIcon,
  ShareEventIcon,
  TagIcon
} from 'assets/icons'
import {
  AnimatePresence,
  motion
} from 'framer-motion'
import {
  Box,
  Image,
  Link,
  Skeleton,
  Spacer,
  Stack,
  Text
} from '@chakra-ui/react'
import { Capacitor } from '@capacitor/core'
import { CapacitorCalendar } from '@ebarooni/capacitor-calendar'
import {
  getEventComments,
  getNotificationMessage,
  markersSelectors,
  saveEvent,
  setBlogLinkToBeCopied,
  setEventComments,
  setEventCommentTree,
  setEventData,
  setChatSectionPage,
  setSaveEventStatus,
  setSharingToFriends,
  toggleImageCarousel,
  usersSelectors
} from 'store'
import { LoadingSpinier } from 'common/Loading'
import { ImagesPng } from 'assets/images'
import {
  convertToOwnTimeZone,
  convertToOwnTimeZoneEvent,
  convertDatetoICSFormat,
  decimalToDMS
} from 'utils/helpers'
import {
  PENDING,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import { createEvent } from 'ics'

import { Comments } from '../Comments'

const MotionStack = motion(Stack)

export const Content = ({
  GoToMarker,
  setShowComments,
  showComments
}) => {
  const dispatch = useDispatch()

  const imageRefs = useRef([])

  const {
    getEventDataStatus,
    eventData
  } = useSelector(markersSelectors)
  const {
    activeProfile,
    eventComments,
    eventCommentTree,
    getEventCommentsStatus,
    postEventCommentStatus,
    saveEventErrorMsg,
    savedEventId,
    saveEventStatus
  } = useSelector(usersSelectors)

  const [directionAddress, setDirectionAddress] = useState('')
  const [saveFontColor, setSaveFontColor] = useState('black')
  const [loadedBI, setLoadedBI] = useState(false)
  const [loadedAI, setLoadedAI] = useState({})
  const [commentsAnimFin, setCommentsAnimFin] = useState(false)

  const formattedSD = convertToOwnTimeZone(eventData?.start_date, 'Y.m.d')
  const formattedED = convertToOwnTimeZone(eventData?.end_date, 'Y.m.d')
  const icsSD = convertDatetoICSFormat(convertToOwnTimeZoneEvent(eventData?.start_date))
  const icsED = convertDatetoICSFormat(convertToOwnTimeZoneEvent(eventData?.end_date))
  const daterange = `${formattedSD}/${formattedED}`
  const deviceSpec = navigator.userAgent

  const idText = eventData?.id?.toLocaleString('en-US', {
    minimumIntegerDigits: 3,
    useGrouping: false
  })

  const convertedCoordinates = () => {
    let convertedLat = decimalToDMS(eventData?.lat, 'lat')
    let convertedLng = decimalToDMS(eventData?.lng, 'lng')

    return convertedLat + ' ' + convertedLng
  }

  const addToRefs = (el) => {
    if (el && !imageRefs?.current.includes(el)) {
      imageRefs?.current.push(el)
    }
  }
  const alarms = [
    {
      action: 'display',
      description: 'An event is about to start in 7 days. Be ready!',
      trigger: { days: 7, before: true },
      repeat: 2
    },
    {
      action: 'display',
      description: 'An event is about to start in 1 day. Be ready!',
      trigger: { hours: 24, before: true },
      repeat: 2
    }
  ]

  const eventICSData = {
    start: icsSD,
    end: icsED,
    title: eventData?.name,
    description: eventData?.description,
    location: eventData?.address || '',
    geo: {
      lat: eventData?.lat,
      lon: eventData?.lng
    },
    categories: eventData?.affinities,
    url: eventData?.affiliate_link,
    alarms
  }

  const handleShare = (id) => {
    sessionStorage.setItem('isSharingBlog', 'true')
    window.dispatchEvent(new CustomEvent('isSharingBlog-changed'))

    dispatch(setChatSectionPage('chat-main'))
    dispatch(setSharingToFriends(true))
    dispatch(setBlogLinkToBeCopied(process.env.REACT_APP_FRONTEND_URL + '/?eventid=' + id))
  }

  const stickyHeader = () => (
    <>
      <Stack
        height="90px !important"
        className="event-popup-division"
        id="event-header-popup"
      >
        <Skeleton
          isLoaded={loadedBI}
          fadeDuration={1}
        >
          <Image
            height="90px !important"
            width="100%"
            objectFit="cover"
            padding="0"
            src={eventData?.background_image ?? ImagesPng.PlaceholderSquare}
            onLoad={() => setLoadedBI(true)}
          />
        </Skeleton>
      </Stack>

      <Stack
        className="event-popup-division"
        id="event-name-popup"
      >
        <Text
          maxWidth="80%"
          maxHeight="4.5em"
          overflow="hidden"
          transition="max-height 0.8s ease"
        >
          { eventData?.name ?? 'Event Unavailable. Please refresh page.'}
        </Text>
        <Spacer />
        <Text className="event-id-text">{idText}</Text>
      </Stack>
    </>
  )

  const handleEventDateClick = () => {
    createEvent(eventICSData, async (error, value) => {
      if (error) {
        console.log(error)
        return
      }

      if (Capacitor.isNativePlatform()) {
        try {
          await CapacitorCalendar.createEventWithPrompt({
            title: eventICSData.title,
            startDate: new Date(convertToOwnTimeZoneEvent(eventData?.start_date)).getTime(),
            endDate: new Date(convertToOwnTimeZoneEvent(eventData?.end_date)).getTime(),
            location: eventICSData.location,
            notes: eventICSData.description,
            alerts: [-1440, -10080]
          })
        } catch (e) {
          console.error(e)
        }
      } else {
        const blob = new Blob([value], { type: 'text/calendar;charset=utf-8' })
        const href = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = href
        link.setAttribute('download', `${eventICSData.title}.ics`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(href)
      }
    })
  }

  useEffect(() => {
    if (
      deviceSpec.includes('iPhone')
      || deviceSpec.includes('Mac')
      || deviceSpec.includes('iPad')
    ) {
      if (eventData?.address !== null && eventData?.address !== '') {
        setDirectionAddress(`http://maps.apple.com/?daddr=${encodeURIComponent(eventData?.address)}`)
      } else if (eventData?.address === null || eventData?.address === '') {
        setDirectionAddress(`https://maps.apple.com/?daddr=${eventData?.lat},${eventData?.lng}`)
      }
    } else if (eventData?.address !== null && eventData?.address !== undefined) {
      setDirectionAddress(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(eventData?.address)}`)
    } else if (eventData?.address === null || eventData?.address === '') {
      setDirectionAddress(`https://www.google.com/maps/dir/${activeProfile?.coordinates[0]},${activeProfile?.coordinates[1]}/${eventData?.lat},${eventData?.lng}`)
    }
  }, [eventData?.address])

  useEffect(() => {
    if (eventData?.id) {
      setCommentsAnimFin(false)
    }
  }, [eventData?.id])

  useEffect(() => {
    if (postEventCommentStatus === SUCCEEDED) {
      dispatch(setEventData({
        ...eventData,
        comment_count: eventData?.comment_count + 1
      }))
    }
  }, [postEventCommentStatus])

  useEffect(() => {
    if (
      eventData?.id === savedEventId
      && (
        saveEventStatus === PENDING
        || saveEventStatus === REJECTED
      )
    ) {
      if (
        saveEventErrorMsg === 'The event is unavailable'
        && saveEventStatus === REJECTED
      ) {
        document.querySelector('#force-close-event-panel')?.click()
        document.querySelector('#custom-popup-overlay-event')?.click()
        dispatch(setSaveEventStatus(''))
      } else {
        if (!eventData?.savedByMe) {
          setSaveFontColor('rgb(231,108,49)')
          setTimeout(() => setSaveFontColor('black'), 1000)
        }

        dispatch(setEventData({
          ...eventData,
          savedByMe: !eventData?.savedByMe
        }))
      }
    }
  }, [saveEventStatus])

  useEffect(() => {
    if (eventCommentTree.length) {
      setShowComments(true)
      const eventCommentData = eventCommentTree.at(-1)

      dispatch(setEventComments([]))
      dispatch(getEventComments({
        event_id: eventCommentData?.event_id ?? eventCommentData?.id,
        ...(eventCommentData?.event_id && { parent_comment_id: eventCommentData?.id })
      }))
    } else {
      setShowComments(false)
    }
  }, [eventCommentTree.length])

  if (getEventDataStatus === PENDING) {
    return (
      <Stack
        position="absolute"
        width="100%"
        marginTop="90px"
      >
        <LoadingSpinier />
      </Stack>
    )
  }

  return (
    eventData?.id
      ? (
        <Stack
          minHeight="100%"
          position="relative"
          width="100%"
        >
          <Box
            id="top-anchor"
            position="absolute"
            top={-50}
          />

          <Stack position="relative" height="100%">
            <AnimatePresence>
              {
                showComments
                && (
                  <MotionStack
                    initial={{ x: 400 }}
                    animate={{ x: 0 }}
                    exit={{ x: 400 }}
                    transition={{
                      type: 'tween',
                      duration: 0.8
                    }}
                    onAnimationComplete={(definition) => {
                      if (definition?.x === 0) {
                        setCommentsAnimFin(true)
                      } else if (definition?.x === 400) {
                        setCommentsAnimFin(false)
                      }
                    }}
                    width="100%"
                    height="100%"
                    zIndex="259"
                  >
                    <Comments
                      eventComments={eventComments}
                      eventCommentTree={eventCommentTree}
                      eventData={eventData}
                      getEventCommentsStatus={getEventCommentsStatus}
                      GoToMarker={GoToMarker}
                      stickyHeader={stickyHeader}
                    />
                  </MotionStack>
                )
              }
            </AnimatePresence>

            <Stack
              position="absolute"
              width="100%"
              height={showComments && commentsAnimFin ? '0px' : ''}
              overflow="hidden"
            >
              { stickyHeader() }

              <Stack padding="0px 20px" className="event-popup-division" id="event-description-popup">
                <Stack className="event-division-item-wrapper-popup">
                  <Text maxWidth="90%">{eventData?.description ? eventData?.description : 'No Description'}</Text>
                </Stack>
              </Stack>

              <Stack
                height="50px"
                className="event-popup-division"
                id="event-date-popup"
                cursor="pointer"
                onClick={() => handleEventDateClick()}
              >
                <Box
                  height="50px"
                  className="event-division-item-wrapper-popup"
                >
                  <EventMarkerIcon start_date={formattedSD.split('.')[1]} color="#000" />
                  <Text>{eventData?.start_date && eventData.end_date ? daterange : 'null'}</Text>
                  <Spacer />
                  <AddCalendarIcon />
                </Box>
              </Stack>

              <Stack height="50px" className="event-popup-division" id="event-address-popup">
                <Box height="50px" className="event-division-item-wrapper-popup">
                  <MarkerGenericIcon />
                  <Text maxWidth="288px" marginY="5px !important">
                    {
                      eventData?.address === null || eventData?.address === ''
                        ? convertedCoordinates()
                        : eventData?.address
                    }
                  </Text>
                </Box>
              </Stack>

              <Stack height="50px" className="event-popup-division">
                <Box height="50px" className="event-division-item-wrapper-popup">
                  <Link
                    href={directionAddress}
                    target="_blank"
                    _cursor="pointer"
                    style={{
                      textDecoration: 'none',
                      color: '#000'
                    }}
                  >
                    <DirectionIcon />
                    <Text>Direction (Maps)</Text>
                  </Link>
                </Box>
              </Stack>

              <Stack height="50px" className="event-popup-division">
                <Link
                  href={eventData?.affiliate_link}
                  target="_blank"
                  _cursor="pointer"
                  style={{
                    textDecoration: 'none',
                    color: '#000'
                  }}
                >
                  <TagIcon />
                  <Text>Tickets (Discount)</Text>
                </Link>
              </Stack>

              <Stack height="50px" className="event-popup-division">
                <Box
                  height="50px"
                  className="event-division-item-wrapper-popup"
                  cursor="pointer"
                  onClick={() => handleShare(eventData?.id)}
                >
                  <ShareEventIcon />
                  <Text>Share</Text>
                </Box>
              </Stack>

              <Stack height="50px" className="event-popup-division">
                <Box
                  as="button"
                  height="50px"
                  textAlign="left"
                  className="event-division-item-wrapper-popup"
                  cursor={saveEventStatus === PENDING ? 'wait' : 'pointer'}
                  onClick={() => {
                    if (
                      saveEventStatus !== PENDING
                      && eventData?.id
                    ) {
                      if (activeProfile?.id) {
                        if (!eventData?.savedByMe) {
                          setSaveFontColor('rgb(231,108,49)')
                          setTimeout(() => setSaveFontColor('black'), 1000)
                        }

                        dispatch(saveEvent({
                          id: eventData?.id,
                          action: eventData?.savedByMe ? 'remove' : 'save'
                        }))
                      } else {
                        dispatch(getNotificationMessage({
                          type: REJECTED,
                          message: 'Create a profile first to save an event',
                          isEditProfile: false
                        }))
                      }
                    }
                  }}
                >
                  <Box flexShrink={0}>
                    <SaveIcon
                      width="24"
                      height="24"
                    />
                  </Box>
                  <Text color={saveFontColor}>
                    {
                      eventData?.savedByMe
                        ? 'Saved in Events row of Notifications Panel'
                        : 'Save'
                    }
                  </Text>
                </Box>
              </Stack>

              <Stack
                height="50px"
                className="event-popup-division"
              >
                <Box
                  height="50px"
                  className="event-division-item-wrapper-popup"
                  cursor="pointer"
                  onClick={() => {
                    if (activeProfile?.id && !eventCommentTree.length) {
                      document.querySelector('#top-anchor').scrollIntoView({
                        behavior: 'instant',
                        block: 'start'
                      })
                      dispatch(setEventCommentTree([...eventCommentTree, eventData]))
                    } else if (!activeProfile?.id) {
                      dispatch(getNotificationMessage({
                        type: REJECTED,
                        message: 'Create a profile first to see comments',
                        isEditProfile: false
                      }))
                    }
                  }}
                >
                  <CommentIcon
                    width="24"
                    height="24"
                  />
                  <Text>
                    Comments
                    {eventData?.comment_count ? ` (${eventData?.comment_count})` : ''}
                  </Text>
                </Box>
              </Stack>

              <Stack
                className="event-popup-division"
                direction="row"
                align="flex-start"
                height="auto"
              >
                <Box flexShrink={0}>
                  <AffinityIcon />
                </Box>

                <Box className="event-division-item-wrapper-popup">
                  <Stack
                    direction="row"
                    wrap="wrap"
                    align="flex-start"
                    gap="6px"
                  >
                    {
                      eventData?.affinities?.map((a) => (
                        <Box
                          key={a}
                          className="event-popup-affinities-container"
                          as="button"
                          cursor="auto"
                        >
                          {a}
                        </Box>
                      ))
                    }
                  </Stack>
                </Box>
              </Stack>

              <Stack id="event-popup-image-wrapper">
                <Stack id="event-popup-image">
                  {eventData?.additional_images?.map((e, index) => (
                    <Skeleton
                      isLoaded={!!loadedAI[e]}
                      fadeDuration={1}
                    >
                      <Image
                        key={e}
                        ref={addToRefs}
                        src={e}
                        maxWidth="320px"
                        borderRadius="5px"
                        cursor="pointer"
                        onClick={() => {
                          dispatch(toggleImageCarousel(true))
                          localStorage.setItem('image-current-slide', index)
                        }}
                        onLoad={() => setLoadedAI((prev) => ({
                          ...prev,
                          [e]: true
                        }))}
                      />
                    </Skeleton>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Stack
          alignSelf="flex-start"
          alignItems="center"
          backgroundColor="rgb(229, 229, 229)"
          borderRadius="3px"
          border="solid 1px black"
          padding="10px"
          margin="10px 2% !important"
          width="100%"
        >
          <Text
            textAlign="center"
            fontSize="13px"
            whiteSpace="pre-line"
            transition="font-size 0.8s, 0.3s"
          >
            This event might be deleted, not existing, or unavailable
          </Text>
        </Stack>
      )
  )
}
